import { useState } from "react"

import { useTranslation } from "react-i18next"

import {
  isSupportedLanguage,
  LANGUAGE_LABEL_KEYS,
  SUPPORTED_LANGUAGES,
  useLanguagePreference
} from "@/entities/user/language"
import { useGetUserSettingsQuery, useUpdateUserSettingsMutation } from "@/entities/user/settings"

import { LanguageFlag } from "@/shared/ui/flag-icon"
import { ModalShell } from "@/shared/ui/modal-shell"

import { useToast } from "@/shared/hooks/useToast"
import { cn } from "@/shared/lib/utils"
import { WebApp } from "@/shared/telegram"
import { Language, type LanguageCode } from "@/shared/types"

const { notificationOccurred } = WebApp.HapticFeedback

export const InitialLanguageSelector = () => {
  const { t, i18n } = useTranslation()
  const toast = useToast()
  const { settingsLoading } = useGetUserSettingsQuery(undefined, {
    selectFromResult: ({ isLoading }) => ({ settingsLoading: isLoading })
  })
  const { language: storedLanguage, isReady, setLanguage: setPreferenceLanguage } = useLanguagePreference()
  const [updateUserSettings] = useUpdateUserSettingsMutation({ selectFromResult: () => ({}) })
  const [pendingLanguage, setPendingLanguage] = useState<LanguageCode | null>(null)
  // Tracks both backend mutation and CloudStorage write — RTK Query's isLoading
  // would clear as soon as the backend resolves, exposing buttons during the cloud half.
  const isSaving = pendingLanguage !== null

  const shouldPrompt = isReady && !settingsLoading && !storedLanguage
  const currentLanguage = i18n.language
  const activeLanguage = pendingLanguage ?? (isSupportedLanguage(currentLanguage) ? currentLanguage : null)

  const handleLanguageSelect = (code: LanguageCode) => {
    if (isSaving) return
    setPendingLanguage(code)

    void (async () => {
      try {
        // Sequence the calls: if the backend rejects we must not have already
        // written to CloudStorage, otherwise next session reads stale local
        // state on top of an outdated backend value.
        await updateUserSettings({ default_language: code }).unwrap()
        await setPreferenceLanguage(code)
        notificationOccurred("success")
        toast.showSuccess(t("language-selector-saved", "Language saved"))
      } catch (error) {
        notificationOccurred("error")
        toast.showError(error, t("language-selector-error", "Unable to save language"))
      } finally {
        setPendingLanguage(null)
      }
    })()
  }

  return (
    <ModalShell
      isOpen={shouldPrompt}
      onClose={() => undefined}
      onBeforeClose={() => false}
      title={t("language-selector-title", "Choose your language")}
      showCloseButton={false}
      disableDrag
      isBlocking
      preserveScrollPosition={false}>
      <div className="px-4 pb-6 pt-2">
        <p className="mb-5 text-sm text-gray-300">
          {t("language-selector-description", "Select the language you want to use in the app.")}
        </p>

        <ul className="grid grid-cols-2 gap-3">
          {SUPPORTED_LANGUAGES.map(code => {
            const isActive = code === activeLanguage
            const labelKey = LANGUAGE_LABEL_KEYS[code]
            const label = t(labelKey, Language[code])

            return (
              <li key={code}>
                <button
                  type="button"
                  disabled={isSaving}
                  onClick={() => {
                    handleLanguageSelect(code)
                  }}
                  className={cn(
                    "flex w-full items-center gap-3 rounded-2xl border border-gray-800/80 bg-gray-900/80 px-4 py-3 text-left transition-all focus-visible:outline focus-visible:outline-2 focus-visible:outline-yellow-500/40 disabled:cursor-not-allowed disabled:opacity-70 hover-device:hover:border-gray-700 hover-device:hover:bg-gray-800/80",
                    isActive && "border-yellow-500/70 bg-yellow-500/10 text-yellow-50"
                  )}>
                  <LanguageFlag
                    code={code}
                    title={label}
                    className="h-6 w-9 rounded-md shadow-sm ring-2 ring-gray-600/50"
                  />
                  <span className="text-sm font-medium">{label}</span>
                </button>
              </li>
            )
          })}
        </ul>

        {isSaving && (
          <p className="mt-4 text-center text-xs text-gray-400">
            {t("language-selector-saving", "Saving your selection…")}
          </p>
        )}
      </div>
    </ModalShell>
  )
}
